import { defineStore } from "pinia";
import { LocationsApi, type LocationModel, type RoomModel } from "@simbelapp/order-sdk";
import type { ICompanyLocationsStore } from "~/utils/interfaces/companies-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { MicroserviceEnum } from "~/utils/enums/common.enums";

export const useCompanyLocationsStore = defineStore("useCompanyLocationsStore", {
  state: (): ICompanyLocationsStore => {
    return {
      locations: [],
    };
  },
  actions: {
    /** Server-searched, paged option source for the location dropdown. */
    async searchRoomsAndLocations(
      params: Record<string, any> = {},
    ): Promise<{ data: LocationModel[]; meta: object | null }> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.getRoomsAndLocations({ ...params, lazyLoad: true });
        return { data: response.data ?? [], meta: response.meta };
      } catch {
        useFeedback().error("Erreur lors de la récupération des salles et des lieux", "small");
        return { data: [], meta: null };
      }
    },

    /** Fetch all rooms and locations and store them. */
    async getLocations() {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const locations = await apiInstance.getRoomsAndLocations();
        if (locations.data) {
          this.locations = locations.data;
        }
      } catch {
        useFeedback().error("Erreur lors de la récupération des salles et des lieux", "small");
      }
    },

    /** Delete a location by id, then refresh the list. */
    async deleteLocation(id: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.removeLocation({ locationId: id });
        await this.getLocations();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la suppression du lieu", "small");
      }
    },

    /** Create a location, then refresh the list. */
    async createLocation(location: LocationModel, longitude?: number, latitude?: number) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.createRoomsLocation({
          createLocationDto: {
            name: location.locationName,
            access: location.locationAccess,
            manualAddress: location.locationManualAddress,
            street: location.locationStreet,
            city: location.locationCity,
            postalCode: location.locationPostalCode,
            state: location.locationState,
            country: location.locationCountry,
            latitude,
            longitude,
          },
        });
        await this.getLocations();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la création du lieu", "small");
      }
    },

    /** Create a room inside a location, then refresh the list. */
    async createRoom(locationId: string, room: RoomModel) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.createRoom({
          locationId,
          createRoomDto: {
            name: room.roomName,
            access: room.roomAccess,
            capacity: room.roomCapacity,
            equipment: room.roomEquipment,
          },
        });
        await this.getLocations();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la création de la salle", "small");
      }
    },

    /** Update a location, then refresh the list. */
    async updateLocation(location: LocationModel, longitude?: number, latitude?: number) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.updateLocation({
          locationId: location.locationId,
          updateLocationDto: {
            name: location.locationName,
            access: location.locationAccess,
            manualAddress: location.locationManualAddress,
            street: location.locationStreet,
            city: location.locationCity,
            postalCode: location.locationPostalCode,
            state: location.locationState,
            country: location.locationCountry,
            latitude,
            longitude,
          },
        });
        await this.getLocations();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la mise à jour du lieu", "small");
      }
    },

    /** Update a room, then refresh the list. */
    async updateRoom(room: RoomModel) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.updateRoom({
          roomId: room.roomId,
          updateRoomDto: {
            name: room.roomName,
            access: room.roomAccess,
            capacity: room.roomCapacity,
            equipment: room.roomEquipment,
          },
        });
        await this.getLocations();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la mise à jour de la salle", "small");
      }
    },

    /** Delete a room by id, then refresh the list. */
    async deleteRoom(id: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, LocationsApi);
        const response = await apiInstance.removeRoom({ roomId: id });
        await this.getLocations();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la suppression de la salle", "small");
      }
    },
  },
});
